import { formGroupsService } from '@services/domain-factories/formGroups';
import { formsService } from '@services/domain-factories/forms';
import { getDataForLinkedTffIterator } from '@services/domain-factories/tables/tableLinkedTff';
import { fetchVisibleTables, getTables } from '@services/domain-factories/tables/tableQueries';
import { fsService } from '@services/firebase/firestore/firestore';
import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { permissionsService, PermissionsService } from '@services/permissions';
import { accountStore } from '@stores/bootstrap-stores/account';
import {
  Entity,
  Form,
  FormGroup,
  FormModel,
  Table,
  TableForm,
  TableItem
} from '@vincerosoft/assessfluence-types';
import { where } from 'firebase/firestore';
import { QuestionDropdownModel, QuestionTagboxModel, SurveyModel } from 'survey-core';

export type LinkedDataForForm = {
  linkedTff: TableForm;
  linkedCollectionItems: TableItem[];
  linkedTable: Table;
};

export type GlobalCollectionsValue = {
  formModel: FormModel | null;
  linkedData: {
    [linkedFormId: string]: LinkedDataForForm;
  };
  linkedForms: Form[];
  linkedFormGroups: FormGroup[];
  isHydrated: boolean;
};

function tableFactory() {
  type Scoped = Pick<Table, 'domain' | 'entity_id' | 'group_entity_id'>;

  // Ownership is the document scope: it must match the caller's own scope.
  function isOwnerOfScope(documentScope: Scoped) {
    const { scope, account } = accountStore.getState();
    return (
      (scope === 'tenant' && documentScope.domain === 'TENANT') ||
      (scope === 'group' && account.id === documentScope.group_entity_id) ||
      (['standalone', 'linked'].includes(scope) && account!.id === documentScope.entity_id)
    );
  }

  // Rules:
  // - TENANT table: tenant adds/views; standalone admin entities add when entities_can_add_items,
  //   and view items they created or that are shared with them. Tenant shares with standalone entities only.
  // - GROUP_ENTITY table: owner adds/views and shares with linked entities; linked admin entities
  //   add when entities_can_add_items, and view items they created or that are shared with them.
  // - ENTITY table: standalone admin adds/views; the entity owns every item and cannot share.
  // - Items created by an entity are owned by that entity (ENTITY scope): the table owner can read
  //   them but cannot edit or share them.
  // A child entity may add items to a tenant table (standalone only) or to its parent group's table.
  function isEligibleEntityForTable(table: Table) {
    const { scope, account } = accountStore.getState();
    const isAdmin = permissionsService.userAccessLevel() === 'admin';
    if (!['standalone', 'linked'].includes(scope) || !isAdmin || !table.entities_can_add_items) {
      return false;
    }

    const linkedGroupId = scope === 'linked' && account?.group_entity_id;
    const isStandalone = scope === 'standalone';

    // Tenant tables: standalone entities only. Group tables: entities linked to that group.
    if (table.domain === 'TENANT') {
      return isStandalone;
    }
    if (table.domain === 'GROUP_ENTITY') {
      return !!linkedGroupId && linkedGroupId === table.group_entity_id;
    }
    return false;
  }

  function canCreateTableItem(table: Table) {
    if (!PermissionsService.canWrite('table-item')) {
      return false;
    }
    if (table.domain === 'ENTITY') {
      return permissionsService.userAccessLevel() === 'admin' && isOwnerOfScope(table);
    }
    return isOwnerOfScope(table) || isEligibleEntityForTable(table);
  }

  // Creating: table owner or eligible entity. Editing/deleting: the item owner only.
  // Being the table owner does not grant write access to an item owned by an entity.
  function hasWriteAccessToTable(table: Table, item?: TableItem | null) {
    if (!item) {
      return canCreateTableItem(table);
    }
    return !!PermissionsService.canWrite('table-item') && isOwnerOfScope(item);
  }

  // Only the item owner can change who an item is shared with. Entity-created items are
  // private to the entity; only tenant- and group-owned items can be shared onward.
  function canShareTableItem(table: Table) {
    return (
      !!PermissionsService.canWrite('table-item') &&
      (table.domain === 'TENANT' || table.domain === 'GROUP_ENTITY')
    );
  }

  function setSurveyDynamicChoices(
    survey: SurveyModel,
    linkedData: { [formId: string]: LinkedDataForForm },
    entities: Entity[] | undefined
  ) {
    const choiceQuestions = survey
      .getAllQuestions()
      .filter((q) => q.getType() === 'tagbox' || q.getType() === 'dropdown');

    if (!choiceQuestions?.length) {
      return;
    }

    const choicesFromEntitiesQ = choiceQuestions.find(
      (q) => q.dynamicChoices === 'choicesFromEntities'
    );

    if (choicesFromEntitiesQ) {
      let choices = [];

      const account = accountStore.getState().account as Entity;

      if (
        account &&
        account.options?.tags?.length &&
        choicesFromEntitiesQ.choicesFromEntitiesFilter &&
        account.options.tags.includes(choicesFromEntitiesQ.choicesFromEntitiesFilter)
      ) {
        choices = [
          {
            value: account.id,
            text: (account as unknown as { company: string })!.company || account.name
          }
        ];
      } else {
        choices = entities?.length
          ? entities
              .filter(
                (ent) =>
                  ent.options?.tags?.length &&
                  choicesFromEntitiesQ.choicesFromEntitiesFilter &&
                  ent.options.tags.includes(choicesFromEntitiesQ.choicesFromEntitiesFilter)
              )
              .map((ent) => {
                return {
                  value: ent.id,
                  text: (ent as unknown as { company: string })!.company || ent.name
                };
              })
          : [];
      }

      (
        survey.getQuestionByName(choicesFromEntitiesQ.name) as
          QuestionTagboxModel | QuestionDropdownModel
      ).choices = choices;

      choicesFromEntitiesQ.choices = choices;
    }

    const choicesFromAnotherTff = choiceQuestions.filter(
      (q) => q.dynamicChoices === 'choicesFromTff' && q.choicesFromTffFormId
    );

    if (choicesFromAnotherTff.length) {
      for (let i = 0; i < choicesFromAnotherTff.length; i++) {
        const question = choicesFromAnotherTff[i];
        const linkedFormId = question.choicesFromTffFormId;

        const { linkedCollectionItems } = linkedData[linkedFormId] || {};

        const items = linkedCollectionItems || [];

        const choices = items.map((item) => {
          return {
            value: item.external_id,
            text: item.label || item.external_id
          };
        });
        (
          survey.getQuestionByName(question.name) as QuestionTagboxModel | QuestionDropdownModel
        ).choices = choices;
        question.choices = choices;
      }
    }
  }

  async function getTable(form: TableForm) {
    const tablesByFormId = await getTables([form]);
    return tablesByFormId.get(form.id);
  }

  async function fetchTablesAndForms() {
    const inFlightStore: {
      tables: Table[];
      forms: TableForm[];
    } = {
      tables: [],
      forms: []
    };

    // Tables first, according to who can see what (see README.md)
    inFlightStore.tables = await fetchVisibleTables();

    const scopeQuery = accountStore.getState().scopeQuery;

    const formChunks = await collectionsService.forms.getDocs<TableForm>([
      scopeQuery,
      where('intent', '==', 'table')
    ]);

    inFlightStore.forms = formChunks.flat();

    // Drop tables whose form is unavailable
    const availableFormIds = new Set(inFlightStore.forms.map((form) => form.id));
    inFlightStore.tables = inFlightStore.tables.filter((table) =>
      availableFormIds.has(table.form_id)
    );

    return inFlightStore;
  }

  async function getAssociatedTableData(selectedForm: TableForm) {
    const inFlightStore: {
      linkedForms: Form[];
      linkedFormGroups: FormGroup[];
      formModel: FormModel | null;
      linkedData: { [formId: string]: LinkedDataForForm };
    } = {
      linkedForms: [],
      linkedFormGroups: [],
      formModel: null,
      linkedData: {}
    };

    inFlightStore.linkedFormGroups = await collectionsService.formGroups.getDocs(
      where('options.linked_table_id', '==', selectedForm.id)
    );

    inFlightStore.linkedForms = await formsService.getLinkedTff(selectedForm.id);

    if (inFlightStore.linkedFormGroups.length > 0) {
      for (const group of inFlightStore.linkedFormGroups) {
        const linkedFormIds = formGroupsService.extractLinkedFormIds([group]);
        const linkedFormsGroupForms = await formsService.getFormsByIds(
          'tablesFactory',
          linkedFormIds
        );
        inFlightStore.linkedForms = inFlightStore.linkedForms.concat(linkedFormsGroupForms);
      }
    }

    inFlightStore.formModel = await collectionsService.formModels.getDoc(
      selectedForm.form_model_id
    );

    const surveyModel = new SurveyModel(inFlightStore.formModel!.json);
    inFlightStore.linkedData = await getDataForLinkedTffIterator(surveyModel);

    return inFlightStore;
  }

  async function getTableByFormId(formId: string) {
    const tables = await collectionsService.tables.getDocs(where('form_id', '==', formId));
    return tables.length > 0 ? tables[0] : null;
  }

  async function getLinkedCampaignsCount(tableId: string) {
    const baseQuery = [where('table_id', '==', tableId), where('status', '==', 'active')];
    return collectionsService.campaigns.getCount(baseQuery);
  }

  async function getLinkedResponsesCount(tableId: string) {
    const baseQuery = [where('table_id', '==', tableId), where('partial', '==', true)];
    return collectionsService.responses.getCount(baseQuery);
  }

  const getTableItem = async (tableName: string, itemId: string) => {
    return fsService.getDoc<TableItem>(tableName, itemId);
  };

  return {
    getTableItem,
    getDataForLinkedTffIterator,
    getTableByFormId,
    setSurveyDynamicChoices,
    getTable,
    getTables,
    fetchTablesAndForms,
    getAssociatedTableData,
    isOwnerOfScope,
    hasWriteAccessToTable,
    canShareTableItem,
    getLinkedCampaignsCount,
    getLinkedResponsesCount
  };
}

export const tableService = tableFactory();
